import React, { useState, useEffect, useMemo } from "react";
import { FaArrowUp, FaArrowDown, FaDownload } from "react-icons/fa";
import * as XLSX from "xlsx";
import { getStock } from "../../api/sync";
import "./Stock.css";

const PAGE_SIZE = 15;

const Stock = () => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "itemNo",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");

  useEffect(() => {
    const fetchStock = async () => {
      try {
        const response = await getStock();
        const items = Array.isArray(response)
          ? response
          : response?.message || [];
        setStock(items);
      } catch (err) {
        console.error("Error fetching stock data:", err);
        setError("Failed to load stock data.");
      } finally {
        setLoading(false);
      }
    };

    fetchStock();
  }, []);

  // Filtered list by search term
  const filteredStock = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return stock;
    return stock.filter((item) => {
      const unit = item.baseUnitOfMeasure || item.baseunitofmeasure || "";
      return (
        String(item.itemNo || "")
          .toLowerCase()
          .includes(term) ||
        String(item.itemName || "")
          .toLowerCase()
          .includes(term) ||
        String(unit).toLowerCase().includes(term) ||
        String(item.locationCode || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [stock, searchTerm]);

  // Sorting
  const sortedStock = useMemo(() => {
    const list = [...filteredStock];
    const { key, direction } = sortConfig || {};
    if (!key) return list;
    list.sort((a, b) => {
      let A = a[key];
      let B = b[key];
      if (key === "baseUnitOfMeasure" || key === "unit") {
        A = a.baseUnitOfMeasure || a.baseunitofmeasure || "";
        B = b.baseUnitOfMeasure || b.baseunitofmeasure || "";
      }
      if (typeof A === "string") A = A.toLowerCase();
      if (typeof B === "string") B = B.toLowerCase();
      if (A === B) return 0;
      const cmp = A > B ? 1 : -1;
      return direction === "asc" ? cmp : -1 * cmp;
    });
    return list;
  }, [filteredStock, sortConfig]);

  // Pagination
  const pageCount = Math.max(1, Math.ceil(sortedStock.length / PAGE_SIZE));
  const pagedStock = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedStock.slice(start, start + PAGE_SIZE);
  }, [sortedStock, currentPage]);

  // Reset to first page when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
    setPageInput("1");
  }, [searchTerm, sortConfig]);

  // Keep page input synced with current page
  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isSameKey = prev?.key === key;
      const nextDirection =
        isSameKey && prev?.direction === "asc" ? "desc" : "asc";
      return { key, direction: nextDirection };
    });
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const value = parseInt(pageInput, 10);
    if (Number.isNaN(value)) return;
    const clamped = Math.min(pageCount, Math.max(1, value));
    setCurrentPage(clamped);
  };

  const handleExportToExcel = () => {
    if (sortedStock.length === 0) {
      alert("No data to export");
      return;
    }

    const exportData = sortedStock.map((item) => ({
      "Item No": item.itemNo || "",
      "Item Name": item.itemName || "",
      Unit: item.baseUnitOfMeasure || item.baseunitofmeasure || "",
      Quantity: item.quantity || "",
      Location: item.locationCode || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock");

    // Set column widths
    worksheet["!cols"] = [
      { wch: 12 },
      { wch: 25 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
    ];

    XLSX.writeFile(
      workbook,
      `Stock_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  if (loading) {
    return <div className="stock-loading">Loading...</div>;
  }

  if (error) {
    return <div className="stock-error">{error}</div>;
  }

  return (
    <div className="stock-container">
      <div className="stock-page-header">
        <h2 className="stock-title">Stock</h2>
        <p className="stock-page-subtitle">View current warehouse stock</p>
      </div>

      {/* Filters */}
      <div className="stock-filters">
        <input
          type="text"
          placeholder="Search by item, unit, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="stock-search-input"
        />
        <button
          className="stock-glass-btn"
          onClick={handleExportToExcel}
          title="Export stock data to Excel"
        >
          <FaDownload /> Export to Excel
        </button>
      </div>

      {sortedStock.length === 0 ? (
        <div className="stock-empty">No stock data available.</div>
      ) : (
        <div className="stock-table-wrapper">
          <table className="stock-table">
            <thead>
              <tr>
                {[
                  { key: "itemNo", label: "Item No" },
                  { key: "itemName", label: "Item Name" },
                  { key: "unit", label: "Unit" },
                  { key: "quantity", label: "Quantity" },
                  { key: "locationCode", label: "Location" },
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    className="stock-glass-header"
                    onClick={() => handleSort(key)}
                  >
                    {label}
                    {sortConfig.key === key && (
                      <span className="stock-sort-icon">
                        {sortConfig.direction === "asc" ? (
                          <FaArrowUp />
                        ) : (
                          <FaArrowDown />
                        )}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedStock.map((item, idx) => (
                <tr key={item.itemNo || idx}>
                  <td>{item.itemNo}</td>
                  <td>{item.itemName}</td>
                  <td>
                    {item.baseUnitOfMeasure || item.baseunitofmeasure || ""}
                  </td>
                  <td>
                    {typeof item.quantity === "number"
                      ? item.quantity.toLocaleString()
                      : item.quantity}
                  </td>
                  <td>{item.locationCode}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="stock-table-footer">
            <div className="stock-row-count">
              Showing {pagedStock.length} of {sortedStock.length}
            </div>
            <div className="stock-pagination">
              <button
                className="stock-glass-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span className="stock-pagination-info">Page</span>
              <input
                className="stock-pagination-input"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pageInput}
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/\D/g, "");
                  setPageInput(onlyNums);
                }}
                onKeyDown={handlePageInputKeyDown}
                aria-label="Current page"
              />
              <span className="stock-pagination-info">of {pageCount}</span>
              <button
                className="stock-glass-btn"
                onClick={() =>
                  setCurrentPage((p) => Math.min(pageCount, p + 1))
                }
                disabled={currentPage >= pageCount}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stock;
